import { useEffect, useRef, useState } from "react"
import { createEmbeddingContext as realCreateContext } from "amazon-quicksight-embedding-sdk"
import { createEmbeddingContext as devMockCreateContext } from "@ciq/shared/utils/quicksight_dev_mock"

const createEmbeddingContext =
  process.env.REACT_APP_QUICKSIGHT_DEV_MOCK === "true"
    ? devMockCreateContext
    : realCreateContext

const DASHBOARD_PAGE_TYPES = new Set(["DASHBOARD", "DASHBOARD_SHEET", "DASHBOARD_VIEW"])

/**
 * Hook to embed a QuickSight console experience and listen for navigation events.
 *
 * @param {Object} options
 * @param {React.RefObject} options.containerRef - Ref to the container DOM element
 * @param {string|null} options.url - The QuickSight embed URL
 * @param {function} options.onNavigate - Called with the full PAGE_NAVIGATION message
 *   when the user navigates to a dashboard-type page. The message always contains
 *   `pageType` (string). At runtime, additional undocumented fields (e.g. dashboardId)
 *   may be present — the consumer should check for these.
 * @returns {{ error: string|null }}
 */
export function useQuickSightEmbed({ containerRef, url, onNavigate }) {
  const onNavigateRef = useRef(onNavigate)
  onNavigateRef.current = onNavigate
  const [error, setError] = useState(null)
  const experienceRef = useRef(null)

  useEffect(() => {
    if (!url || !containerRef.current) return

    const container = containerRef.current
    let cancelled = false

    async function embed() {
      try {
        const embeddingContext = await createEmbeddingContext()

        if (cancelled) return

        const experience = await embeddingContext.embedConsole(
          {
            url,
            container,
            height: "100%",
            width: "100%",
          },
          {
            onMessage: (messageEvent) => {
              if (
                messageEvent.eventName === "PAGE_NAVIGATION" &&
                DASHBOARD_PAGE_TYPES.has(messageEvent.message?.pageType)
              ) {
                onNavigateRef.current(messageEvent.message)
              }
            },
          }
        )

        if (!cancelled) {
          experienceRef.current = experience
          setError(null)
        }
      } catch (e) {
        if (!cancelled) {
          console.error("QuickSight SDK embedding failed:", e)
          setError("Unable to load dashboard")
        }
      }
    }

    embed()

    return () => {
      cancelled = true
      if (experienceRef.current?.close) {
        experienceRef.current.close().catch(() => {})
      }
      experienceRef.current = null
      container.innerHTML = ""
    }
  }, [url, containerRef])

  return { error }
}
